import React, { useState } from "react";

import { recordConsent } from "./integrityClient";

/**
 * Biometric consent gate (BIPA / GDPR Art.9) — affirmative opt-in shown BEFORE any capture
 * runs. Decline proceeds without monitoring; the decision is recorded server-side with the
 * notice version for audit.
 *
 * DESIGN RULE — the notice is DERIVED FROM LIVE STATE, never hardcoded.
 * BIPA 740 ILCS 14/15(b) requires that, before collection, the subject is informed in writing
 * of (i) that a biometric identifier is being collected/stored, (ii) the specific purpose, and
 * (iii) the length of term it is collected/stored/used for — and gives a written release.
 * A fixed notice cannot satisfy (i)-(iii) when the capture scope is flag-controlled: it would
 * either over-claim (describing capture that is switched off) or under-claim (omitting capture
 * that is switched on). Under-claiming is the one that creates liability, because the release
 * we hold would not cover what we actually did.
 *
 * So: `capabilities` describes what is ACTUALLY enabled for this session, every clause is
 * conditional on it, and the recorded notice version embeds the enabled scope. Turning on a
 * new capture category therefore yields a NEW version string, which makes previously-recorded
 * consent non-matching for the wider scope rather than silently reused.
 *
 * Copy version is bumped whenever the wording changes; scope is appended automatically.
 */
export const CONSENT_NOTICE_VERSION = "2026-08-07-v3";

/** Retention terms surfaced to the candidate. Must mirror the server's actual sweeper config. */
export const RETENTION_TERMS = {
  faceTemplateDays: 30,
  // Enforced window is 30d (platform_config integrity.evidence.retention_hours=720, swept by
  // integrity-service; UM fraud-snapshot backstop is also 30d). TI-1071.
  evidenceFrameDays: 30,
  transcriptDays: 30,
};

/**
 * Stable, human-auditable scope key. Sorted so the string is deterministic, and embedded in
 * the recorded notice version: consent granted for `identity` does not read as consent for
 * `identity+audio`.
 */
export function consentScopeKey(capabilities) {
  const active = [];
  if (capabilities.identityVerification) active.push("identity");
  if (capabilities.presenceMonitoring) active.push("presence");
  if (capabilities.evidenceKinds && capabilities.evidenceKinds.length > 0) active.push("evidence");
  if (capabilities.audioTranscription) active.push("audio");
  return active.length ? active.sort().join("+") : "none";
}

export function noticeVersionFor(capabilities) {
  return `${CONSENT_NOTICE_VERSION}/${consentScopeKey(capabilities)}`;
}

const KIND_LABELS = {
  multiple_faces: "more than one person appearing on camera",
  tab_switch: "switching away from the interview tab",
  no_face: "your face not being visible",
  looking_away: "looking away from the screen for long periods",
};

export default function IntegrityConsent({ candidateId, jwt, capabilities, onDecision }) {
  const [submitting, setSubmitting] = useState(false);

  // Fail CLOSED on description: if we were handed no capability description, describe the
  // narrowest thing we could be doing rather than assuming the full suite is off.
  const caps = capabilities || { identityVerification: true, presenceMonitoring: true, evidenceKinds: [] };
  const kinds = (caps.evidenceKinds || []).filter((k) => KIND_LABELS[k]);
  const noticeVersion = noticeVersionFor(caps);

  const decide = async (granted) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await recordConsent({ candidateId, granted, noticeVersion, jwt });
    } catch (_) {
      // Best-effort audit; proceed with the candidate's local decision regardless.
    }
    onDecision(granted);
  };

  return (
    <div
      className="integrity-consent-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="integrity-consent-title"
    >
      <div className="integrity-consent-card">
        <h2 id="integrity-consent-title">Identity verification</h2>

        <p>
          Before this interview starts, we need your permission to check that the person taking it
          is you. You can decline — the interview continues either way.
        </p>

        <h3>What we collect</h3>
        <ul>
          {caps.identityVerification && (
            <li>
              A <strong>face template</strong> — a mathematical representation of your facial
              features, created from your camera image. This is biometric information.
            </li>
          )}
          {caps.presenceMonitoring && (
            <li>
              Periodic camera images during the interview, compared against that template to confirm
              the same person is still present.
            </li>
          )}
          {kinds.length > 0 && (
            <li>
              A still image saved only if the system detects{" "}
              {kinds.map((k) => KIND_LABELS[k]).join(", ")} — kept as evidence for that specific
              flag, so a decision about your interview can be reviewed.
            </li>
          )}
          {caps.audioTranscription && (
            <li>
              A <strong>text transcript of your spoken answers</strong>. Audio is transcribed for
              interview assessment. We do not create a voiceprint.
            </li>
          )}
        </ul>

        <h3>Why</h3>
        <p>
          Solely to confirm your identity and the integrity of this interview
          {kinds.length > 0 ? ", and to evidence any integrity flag raised against you" : ""}. We do
          not use it to train models, profile you, or for advertising.
        </p>

        <h3>How long we keep it</h3>
        <ul>
          {caps.identityVerification && (
            <li>Face template: deleted {RETENTION_TERMS.faceTemplateDays} days after this interview.</li>
          )}
          {kinds.length > 0 && (
            <li>
              Evidence images: deleted {RETENTION_TERMS.evidenceFrameDays} days after this interview,
              so you have time to dispute a flag.
            </li>
          )}
          {caps.audioTranscription && (
            <li>Transcript: deleted {RETENTION_TERMS.transcriptDays} days after this interview.</li>
          )}
          <li>
            Sooner if you ask us to delete it, or once the purpose above is satisfied — whichever
            comes first.
          </li>
        </ul>

        <h3>Who sees it</h3>
        <p>
          Only theinterviews.ai and the service providers who host this platform on our behalf. We
          do not sell, lease, trade, or otherwise profit from your biometric information, and we do
          not disclose it to anyone else without your consent or unless required by law.
        </p>

        <h3>Your choice</h3>
        <ul>
          <li>
            <strong>Decline</strong> — nothing above is collected. The interview proceeds without
            identity verification and your result is not penalised for declining.
          </li>
          <li>
            You can withdraw consent at any time by contacting{" "}
            <a href="mailto:support@theinterviews.ai">support@theinterviews.ai</a>, and we will
            delete what we hold.
          </li>
        </ul>

        <p className="integrity-consent-fineprint">
          By selecting “I agree” you provide a written release for the collection, storage, and use
          described above, for this interview only. Notice version {noticeVersion}.
        </p>

        <div className="integrity-consent-actions">
          {/* Room's shared button system (TI-1100) — same classes the stop-recording dialog
              uses, so this reads as part of the room rather than a bolted-on web form. */}
          <button
            type="button"
            className="control-btn"
            disabled={submitting}
            onClick={() => decide(false)}
          >
            Decline
          </button>
          <button
            type="button"
            className="control-btn primary"
            disabled={submitting}
            onClick={() => decide(true)}
          >
            I agree
          </button>
        </div>
      </div>
    </div>
  );
}
